/**
 * Bar widths for the "Usage by feature" list.
 *
 * The bars compare features against each other, not against the allowance: the point
 * is "Chat is using most of it", and a feature that consumed 1% of the allowance
 * would otherwise render as an invisible sliver next to its own number. So the
 * largest feature fills the track and the rest are drawn in proportion to it.
 */

export interface UsageBarInput {
    creditsUsed: number;
}

export type WithBarPercent<T> = T & { barPercent: number };

/**
 * Any bar with a non-zero value gets at least this much width, so a small-but-real
 * figure still reads as a bar rather than as nothing at all.
 */
export const MIN_VISIBLE_BAR_PERCENT = 2;

export function withBarPercents<T extends UsageBarInput>(items: T[] | null | undefined): WithBarPercent<T>[] {
    if (!items || !items.length) return [];

    const largest = items.reduce((max, item) => Math.max(max, item.creditsUsed || 0), 0);

    // Every feature on zero: nothing to compare, so draw no bars rather than drawing
    // them all full.
    if (largest <= 0) return items.map(item => ({ ...item, barPercent: 0 }));

    return items.map(item => {
        const used = item.creditsUsed || 0;
        if (used <= 0) return { ...item, barPercent: 0 };
        return { ...item, barPercent: Math.max(MIN_VISIBLE_BAR_PERCENT, Math.round((used / largest) * 100)) };
    });
}
